import React from 'react';
import { Spin, Form, Input, Select, InputNumber, Row, Col, DatePicker, Switch } from 'antd';
import { 
    InfoCircleOutlined, 
    SettingOutlined, 
    DollarOutlined,
    CalendarOutlined,
    UserOutlined,
    AppstoreOutlined,
    StopOutlined
} from '@ant-design/icons';
import { 
    ContentCard, 
    ContentCardHeader, 
    ContentCardTitle, 
    MetaValueInherit, 
    DetailLabel,
    MutedText,
    SettingsModulesGrid,
    InteractiveClientCard
} from '@/pages/inside/tenant/style';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import { 
    useProjectSettings, 
    PRIORITY_OPTIONS,
    ALL_MODULES_CONFIG
} from '@hooks/projects/settings/useProjectSettings';
import { colors } from '@/utils/colorSystem';

// Import modular settings tab components
import ModuleToggleControl from './ModuleToggleControl';
import TemplateSelectModal from './TemplateSelectModal';
import ClientSelectModal from './ClientSelectModal';
import DeleteProjectModal from './DeleteProjectModal';

export default function ProjectSettingsForm({ projectId }) {
    const { 
        settings, 
        isLoading, 
        isError, 
        canUpdate, 
        updatePending,
        templates,
        templatesLoading,
        projectTypeOptions,
        categoryOptions,
        clientOptions,
        optionsLoading,
        clients,
        clientsLoading,
        onClientsDropdownOpen,
        form,
        handleSave,
        templateModalOpen,
        setTemplateModalOpen,
        handleTemplateSelect,
        canDelete,
        deleteProject,
        isDeleting,
        deleteModalOpen,
        setDeleteModalOpen,
        clientModalOpen,
        setClientModalOpen,
        clientSearch,
        setClientSearch,
        tempClientId,
        setTempClientId,
        handleClientModalOpen,
        handleClientModalSave,
        handleTemplateModalOpen,
        handleDeleteProject,
        filteredClients,
        activeClient,
        activeClientTypeStyle
    } = useProjectSettings(projectId);

    const currentTemplateId = Form.useWatch('projectTemplateId', form);
    const currentTemplate = templates.find(t => t.id === currentTemplateId);
    const showMilestonesRule = currentTemplate ? !!currentTemplate.useMilestones : true;
    const showSprintsRule = currentTemplate ? !!currentTemplate.useSprints : false;
    const hasVisibleModules = ALL_MODULES_CONFIG.some(mod => settings.modules?.[mod.key]?.allowed);

    if (isLoading) {
        return (
            <Container $display="flex" $flexDirection="column" $alignItems="center" $justifyContent="center" $minHeight="300px" $width="100%" $gap="12px">
                <Spin size="large" />
            </Container>
        );
    }

    if (isError) {
        return (
            <ContentCard $padding="24px">
                <MetaValueInherit>Failed to load project settings.</MetaValueInherit>
            </ContentCard>
        );
    }

    return (
        <Form
            form={form}
            layout="vertical"
            onValuesChange={(changedValues, allValues) => {
                const IMMEDIATE_KEYS = [
                    'projectTemplateId',
                    'projectType',
                    'categoryId',
                    'clientId',
                    'startDate',
                    'endDate',
                    'milestoneDueDateRule',
                    'sprintDateRule',
                    'priority',
                    'isQA',
                    'isPMO',
                    'isBlockers',
                    'isCollaboration',
                    'isBacklogs',
                    'isChangeRequest',
                    'isReviews',
                    'isStarred',
                ];
                const changedKeys = Object.keys(changedValues);
                const hasImmediate = changedKeys.some(key => IMMEDIATE_KEYS.includes(key));
                handleSave(allValues, hasImmediate);
            }}
            requiredMark={false}
            disabled={!canUpdate}
        >
            <Container $display="flex" $flexDirection="column" $gap="24px" $maxWidth="900px">
                {/* Identity & Classification */}
                <ContentCard $marginBottom="0" $overflow="hidden">
                    <ContentCardHeader>
                        <Container $display="flex" $alignItems="center" $gap="8px">
                            <InfoCircleOutlined style={{ fontSize: 18, color: 'var(--ant-color-primary)' }} />
                            <ContentCardTitle>Identity & Classification</ContentCardTitle>
                        </Container>
                    </ContentCardHeader>
                    
                    <Container $padding="20px 24px">
                        <Row gutter={24}>
                            <Col span={16}>
                                <Form.Item
                                    name="name"
                                    label={<DetailLabel>Project Name</DetailLabel>}
                                    rules={[{ required: true, message: 'Please enter project name' }, { max: 255 }]}
                                >
                                    <Input placeholder="Enter project name" />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    name="slug"
                                    label={<DetailLabel>Slug</DetailLabel>}
                                    rules={[{ max: 80 }]}
                                >
                                    <Input placeholder="e.g. PRJ" />
                                </Form.Item>
                            </Col>
                        </Row>
                        
                        <Row gutter={24}>
                            <Col span={24}>
                                <Form.Item
                                    name="isStarred"
                                    valuePropName="checked"
                                    label={<DetailLabel>Starred Project</DetailLabel>}
                                >
                                    <Switch checkedChildren="Starred" unCheckedChildren="Unstarred" />
                                </Form.Item>
                            </Col>
                        </Row>
                        
                        <Row gutter={24}>
                            <Col span={24}>
                                <Form.Item
                                    name="projectTemplateId"
                                    label={<DetailLabel>Project Template</DetailLabel>}
                                >
                                    <Container 
                                        $padding="16px 20px"
                                        $border={`1px solid ${colors.neutral[200]}`}
                                        $borderRadius="12px"
                                        $backgroundColor={colors.neutral[50]}
                                        $display="flex"
                                        $justifyContent="space-between"
                                        $alignItems="center"
                                        $minHeight="74px"
                                    >
                                        <Container $display="flex" $alignItems="center" $gap="12px" $minWidth="0" $flex="1">
                                            <Container 
                                                $width="42px" 
                                                $height="42px" 
                                                $borderRadius="50%" 
                                                $backgroundColor={colors.primary[50]} 
                                                $display="flex" 
                                                $alignItems="center" 
                                                $justifyContent="center" 
                                                $border={`1px solid ${colors.primary[100]}`}
                                                $flex="0 0 auto"
                                            >
                                                <AppstoreOutlined style={{ color: colors.primary[600], fontSize: 18 }} />
                                            </Container>
                                            <Container $minWidth="0" $flex="1">
                                                <div style={{ fontWeight: 700, fontSize: 14, color: colors.neutral[900], whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {currentTemplate?.name || 'Standard Project'}
                                                </div>
                                                <Container $display="flex" $gap="4px" style={{ fontSize: 11, color: colors.neutral[400], marginTop: 2 }}>
                                                    {currentTemplate ? (
                                                        <>
                                                            {currentTemplate.useMilestones && <span style={{ background: colors.success?.[50] || '#ecfdf5', color: colors.success?.[700] || '#047857', padding: '1px 6px', borderRadius: 4, fontSize: '9px', fontWeight: 600 }}>Milestones</span>}
                                                            {currentTemplate.useSprints && <span style={{ background: '#f5f3ff', color: '#6d28d9', padding: '1px 6px', borderRadius: 4, fontSize: '9px', fontWeight: 600 }}>Sprints</span>}
                                                            {!currentTemplate.useMilestones && !currentTemplate.useSprints && <span style={{ background: colors.neutral[100], color: colors.neutral[600], padding: '1px 6px', borderRadius: 4, fontSize: '9px', fontWeight: 600 }}>Basic</span>}
                                                        </>
                                                    ) : (
                                                        <span style={{ background: colors.neutral[100], color: colors.neutral[600], padding: '1px 6px', borderRadius: 4, fontSize: '9px', fontWeight: 600 }}>Default Template</span>
                                                    )}
                                                </Container>
                                            </Container>
                                        </Container>
                                        {canUpdate && (
                                            <Button 
                                                onClick={handleTemplateModalOpen}
                                                style={{ 
                                                    height: 28, 
                                                    borderRadius: 6, 
                                                    fontSize: '11px', 
                                                    fontWeight: 600, 
                                                    padding: '0 10px',
                                                    background: 'transparent',
                                                    border: `1px solid ${colors.primary[200]}`,
                                                    color: colors.primary[700],
                                                    boxShadow: 'none',
                                                    flexShrink: 0
                                                }}
                                            >
                                                Change
                                            </Button>
                                        )}
                                    </Container>
                                    <Form.Item name="projectTemplateId" noStyle><Input type="hidden" /></Form.Item>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={24}>
                            <Col span={12}>
                                <Form.Item
                                    name="projectType"
                                    label={<DetailLabel>Project Type</DetailLabel>}
                                    rules={[{ required: true, message: 'Please pick a project type' }]}
                                >
                                    <Select placeholder="Select type" options={projectTypeOptions} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="categoryId"
                                    label={<DetailLabel>Category</DetailLabel>}
                                >
                                    <Select 
                                        placeholder="Select category" 
                                        options={categoryOptions} 
                                        loading={optionsLoading}
                                        allowClear
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Container>
                </ContentCard>

                {/* Client & Financials */}
                <ContentCard $marginBottom="0" $overflow="hidden">
                    <ContentCardHeader>
                        <Container $display="flex" $alignItems="center" $gap="8px">
                            <UserOutlined style={{ fontSize: 18, color: 'var(--ant-color-primary)' }} />
                            <ContentCardTitle>Client & Financials</ContentCardTitle>
                        </Container>
                    </ContentCardHeader>
                    
                    <Container $padding="20px 24px">
                        <Row gutter={24} style={{ display: 'flex', alignItems: 'stretch' }}>
                            <Col span={12}>
                                <DetailLabel style={{ marginBottom: 8, display: 'block' }}>Project Client</DetailLabel>
                                <InteractiveClientCard 
                                    $canUpdate={canUpdate}
                                    onClick={canUpdate ? handleClientModalOpen : undefined}
                                >
                                    <Container $display="flex" $alignItems="center" $gap="12px" $minWidth="0" $flex="1">
                                        <Container 
                                            $width="42px" 
                                            $height="42px" 
                                            $borderRadius="50%" 
                                            $backgroundColor={colors.primary[50]} 
                                            $display="flex" 
                                            $alignItems="center" 
                                            $justifyContent="center" 
                                            $border={`1px solid ${colors.primary[100]}`}
                                            $flex="0 0 auto"
                                        >
                                            <UserOutlined style={{ color: colors.primary[600], fontSize: 18 }} />
                                        </Container>
                                        <Container $minWidth="0" $flex="1">
                                            <Container $display="flex" $alignItems="center" $gap="8px" $flexWrap="wrap">
                                                <span style={{ fontWeight: 700, fontSize: 14, color: colors.neutral[900], whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '140px' }}>
                                                    {activeClient?.name || 'No Client Assigned'}
                                                </span>
                                                {activeClientTypeStyle && (
                                                    <span style={{ 
                                                        fontSize: '9px', 
                                                        fontWeight: 600, 
                                                        padding: '2px 8px', 
                                                        borderRadius: '12px', 
                                                        background: activeClientTypeStyle.bg, 
                                                        color: activeClientTypeStyle.color,
                                                        whiteSpace: 'nowrap',
                                                        flexShrink: 0
                                                    }}>
                                                        {activeClientTypeStyle.label}
                                                    </span>
                                                )}
                                            </Container>
                                            {activeClient?.email && (
                                                <div style={{ fontSize: 11, color: colors.neutral[400], marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {activeClient.email}
                                                </div>
                                            )}
                                        </Container>
                                    </Container>
                                </InteractiveClientCard>
                                <Form.Item name="clientId" noStyle><Input type="hidden" /></Form.Item>
                            </Col>
                            <Col span={12}>
                                <DetailLabel style={{ marginBottom: 8, display: 'block' }}>Project Budget</DetailLabel>
                                <Container 
                                    $backgroundColor={colors.neutral[50]} 
                                    $border={`1px solid ${colors.neutral[200]}`} 
                                    $borderRadius="12px" 
                                    $padding="16px 20px" 
                                    $display="flex" 
                                    $flexDirection="column"
                                    $justifyContent="center"
                                    $minHeight="74px"
                                >
                                    <Form.Item name="budget" rules={[{ type: 'number', min: 0 }]} noStyle>
                                        <InputNumber
                                            style={{ width: '100%', fontSize: '18px', fontWeight: '700', background: 'transparent' }}
                                            prefix={<DollarOutlined style={{ color: colors.success?.[500] || '#10b981', marginRight: 4 }} />}
                                            bordered={false}
                                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                            parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                            placeholder="Enter budget"
                                        />
                                    </Form.Item>
                                </Container>
                            </Col>
                        </Row>
                    </Container>
                </ContentCard>

                {/* Timeline */}
                <ContentCard $marginBottom="0" $overflow="hidden">
                    <ContentCardHeader>
                        <Container $display="flex" $alignItems="center" $gap="8px">
                            <CalendarOutlined style={{ fontSize: 18, color: 'var(--ant-color-primary)' }} />
                            <ContentCardTitle>Timeline</ContentCardTitle>
                        </Container>
                    </ContentCardHeader>
                    
                    <Container $padding="20px 24px">
                        <Row gutter={24}>
                            <Col span={12}>
                                <Form.Item
                                    name="startDate"
                                    label={<DetailLabel>Start Date</DetailLabel>}
                                >
                                    <DatePicker style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="endDate"
                                    label={<DetailLabel>End Date</DetailLabel>}
                                >
                                    <DatePicker style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Container>
                </ContentCard>

                {/* Workflow & Scheduling Rules */}
                <ContentCard $marginBottom="0" $overflow="hidden">
                    <ContentCardHeader>
                        <Container $display="flex" $alignItems="center" $gap="8px">
                            <SettingOutlined style={{ fontSize: 18, color: 'var(--ant-color-primary)' }} />
                            <ContentCardTitle>Workflow & Scheduling Rules</ContentCardTitle>
                        </Container>
                    </ContentCardHeader>
                    
                    <Container $padding="20px 24px">
                        {(showMilestonesRule || showSprintsRule) && (
                            <Row gutter={24}>
                                {showMilestonesRule && (
                                    <Col span={12}>
                                        <Form.Item
                                            name="milestoneDueDateRule"
                                            label={<DetailLabel>Milestone Due Date Rule</DetailLabel>}
                                            extra={<MutedText style={{ fontSize: 11 }}>Strict: ordered dates. Relaxing: any order.</MutedText>}
                                        >
                                            <Select>
                                                <Select.Option value="strict">Strict</Select.Option>
                                                <Select.Option value="relaxing">Relaxing</Select.Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                )}
                                {showSprintsRule && (
                                    <Col span={12}>
                                        <Form.Item
                                            name="sprintDateRule"
                                            label={<DetailLabel>Sprint Date Rule</DetailLabel>}
                                            extra={<MutedText style={{ fontSize: 11 }}>Strict: no overlaps. Relaxing: allows overlaps.</MutedText>}
                                        >
                                            <Select>
                                                <Select.Option value="strict">Strict</Select.Option>
                                                <Select.Option value="relaxing">Relaxing</Select.Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                )}
                            </Row>
                        )}
                        
                        <Row gutter={24}>
                            <Col span={12}>
                                <Form.Item
                                    name="priority"
                                    label={<DetailLabel>Priority</DetailLabel>}
                                >
                                    <Select options={PRIORITY_OPTIONS} />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Container>
                </ContentCard>

                {/* Project Modules */}
                {hasVisibleModules && (
                    <ContentCard $marginBottom="0" $overflow="hidden">
                        <ContentCardHeader>
                            <Container $display="flex" $alignItems="center" $gap="8px">
                                <SettingOutlined style={{ fontSize: 18, color: 'var(--ant-color-primary)' }} />
                                <ContentCardTitle>Project Modules</ContentCardTitle>
                            </Container>
                        </ContentCardHeader>
                        
                        <Container $padding="20px 24px">
                            <SettingsModulesGrid>
                                {ALL_MODULES_CONFIG.map(mod => {
                                    const meta = settings.modules?.[mod.key] || { allowed: false, enabled: false };
                                    if (!meta.allowed) return null;

                                    return (
                                        <Form.Item key={mod.key} name={mod.key} valuePropName="checked" noStyle>
                                            <ModuleToggleControl mod={mod} disabled={!canUpdate} />
                                        </Form.Item>
                                    );
                                })}
                            </SettingsModulesGrid>
                            {currentTemplate && (currentTemplate.submissionMode === 'memo' || !!currentTemplate.useSubmissionMemo) && (
                                <div style={{ marginTop: '12px', padding: '12px 16px', background: colors.warning?.[50] || '#fffbeb', border: `1px solid ${colors.warning?.[100] || '#fef3c7'}`, borderRadius: '8px', fontSize: '12px', color: colors.warning?.[700] || '#b45309', lineHeight: 1.5 }}>
                                    The Submission Memo template is designed for submission workflows without QA Testing or PMO modules. If QA or PMO features are required, please select a different project template.
                                </div>
                            )}
                        </Container>
                    </ContentCard>
                )}

                {/* Danger Zone */}
                {canDelete && (
                    <ContentCard $marginBottom="0" $overflow="hidden" style={{ borderColor: colors.error?.[200] || '#fecaca', background: colors.error?.[50] || '#fef2f2' }}>
                        <ContentCardHeader style={{ borderBottom: `1px solid ${colors.error?.[100] || '#fee2e2'}` }}>
                            <Container $display="flex" $alignItems="center" $gap="8px">
                                <StopOutlined style={{ fontSize: 18, color: colors.error?.[500] || '#ef4444' }} />
                                <ContentCardTitle style={{ color: colors.error?.[700] || '#b91c1c' }}>Danger Zone</ContentCardTitle>
                            </Container>
                        </ContentCardHeader>
                        
                        <Container $padding="20px 24px" $display="flex" $justifyContent="space-between" $alignItems="center">
                            <div>
                                <div style={{ fontWeight: 700, fontSize: 14, color: colors.neutral[900] }}>Delete this project</div>
                                <MutedText style={{ fontSize: 12, marginTop: 4 }}>Once you delete a project, there is no going back. Please be certain.</MutedText>
                            </div>
                            <Button 
                                $dangerOutline
                                loading={isDeleting}
                                onClick={handleDeleteProject}
                            >
                                Delete Project
                            </Button>
                        </Container>
                    </ContentCard>
                )}

                {/* Saving Indicator */}
                {updatePending && (
                    <Container $display="flex" $justifyContent="flex-end" $marginTop="8px" $alignItems="center" $gap="8px" style={{ color: colors.neutral[400], fontSize: '13px' }}>
                        <Spin size="small" />
                        <span>Saving changes...</span>
                    </Container>
                )}
            </Container>

            {/* Template Selection Modal */}
            <TemplateSelectModal
                open={templateModalOpen}
                onCancel={() => setTemplateModalOpen(false)}
                templatesLoading={templatesLoading}
                currentTemplateId={currentTemplateId}
                handleTemplateSelect={handleTemplateSelect}
                templates={templates}
            />

            {/* Client Selection Modal */}
            <ClientSelectModal
                open={clientModalOpen}
                onCancel={() => setClientModalOpen(false)}
                clientSearch={clientSearch}
                setClientSearch={setClientSearch}
                clientsLoading={clientsLoading}
                filteredClients={filteredClients}
                tempClientId={tempClientId}
                setTempClientId={setTempClientId}
                handleClientModalSave={handleClientModalSave}
            />

            {/* Delete Confirmation Modal */}
            <DeleteProjectModal
                open={deleteModalOpen}
                onCancel={() => setDeleteModalOpen(false)}
                onConfirm={deleteProject}
                projectName={settings.name || ''}
                isDeleting={isDeleting}
            />
        </Form>
    );
}
